import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { performanceService } from '../../services/performanceService';
import { useSnackbar } from '../../utils/useSnackbar';
import TableSkeleton from '../common/TableSkeleton';
import PageHeader from '../ui/PageHeader';
import {
  REVIEW_TYPE,
  REVIEW_TYPE_LABELS,
  REVIEW_TYPE_TABS,
  REVIEW_TYPE_HAS_MILESTONE,
  REVIEW_STATUS,
  REVIEW_STATUS_OPTIONS,
} from '../../constants/reviewType';
import '../../css/Leave.css';

const fmtDate = (val) => {
  if (!val) return '—';
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
};

const statusChipProps = (status) => {
  switch (status) {
    case REVIEW_STATUS.COMPLETED: return { color: 'success', label: 'Completed' };
    case REVIEW_STATUS.EMPLOYEE_SUBMITTED: return { color: 'info', label: 'Submitted' };
    case REVIEW_STATUS.SKIPPED: return { color: 'default', label: 'Skipped' };
    default: return { color: 'warning', label: 'Pending' };
  }
};

const SelfEvaluation = () => {
  const { data: userData } = useSelector((state) => state.userDetails);
  const userRoles = useMemo(() => userData?.roles ?? [], [userData]);
  const userId = userData?.id;
  const navigate = useNavigate();

  // ── Filters ────────────────────────────────────────────────────────────
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const reviewType = REVIEW_TYPE.QUARTERLY_PERFORMANCE_REVIEW;
  const showMilestone = REVIEW_TYPE_HAS_MILESTONE.includes(reviewType);

  // ── Pagination / sort ─────────────────────────────────────────────────
  const [records, setRecords] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([{ field: 'createdAt', sort: 'desc' }]);
  const isSearchFirstRender = useRef(true);
  const isSortFirstRender = useRef(true);

  const { showSnackbar, SnackbarComponent } = useSnackbar();

  // ── Debounce search ───────────────────────────────────────────────────
  useEffect(() => {
    if (isSearchFirstRender.current) { isSearchFirstRender.current = false; return; }
    const t = setTimeout(() => {
      const v = search.trim();
      if (v === '' || v.length > 2) {
        setDebouncedSearch(v);
        setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
      }
    }, 500);
    return () => clearTimeout(t);
  }, [search]);

  // ── Fetch ─────────────────────────────────────────────────────────────
  const fetchReviews = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const sortField = sortModel?.[0]?.field || 'createdAt';
      const sortDir = sortModel?.[0]?.sort || 'desc';

      // For standard employees, the backend should return their own reviews based on userId
      const data = await performanceService.getReviewsByType(
        userId, [], // pass empty roles array so it fetches as a standard user if backend uses roles for filtering
        reviewType, status, debouncedSearch,
        paginationModel.page, paginationModel.pageSize,
        sortField, sortDir,
      );
      setRecords(data?.content ?? []);
      setRowCount(data?.totalElements ?? 0);
    } catch (err) {
      if (err?.response?.status === 404) {
        setRecords([]); setRowCount(0);
      } else {
        const msg = err?.response?.data?.message || err?.response?.data || 'Failed to load reviews.';
        showSnackbar(typeof msg === 'string' ? msg : 'Something went wrong', 'error');
        setRecords([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, reviewType, status, debouncedSearch, paginationModel, sortModel]);

  // Reset page when status changes
  useEffect(() => {
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  }, [status]);

  const handleRefresh = () => {
    setSearch('');
    setDebouncedSearch('');
    setStatus('');
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
    fetchReviews();
  };

  const handleSortModelChange = (newModel) => {
    if (isSortFirstRender.current) { isSortFirstRender.current = false; if (!newModel?.length) return; }
    const next = newModel?.[0]; const current = sortModel?.[0];
    if (next?.field === current?.field && next?.sort === current?.sort) return;
    setSortModel(newModel);
    setPaginationModel((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  };

  // ── Columns ───────────────────────────────────────────────────────────
  const columns = useMemo(() => [
    {
      field: 'userName',
      headerName: 'Employee',
      flex: 1,
      renderCell: (p) => (
        <Tooltip title={p.value ?? ''}><span className="leave-ellipsis">{p.value ?? '—'}</span></Tooltip>
      ),
    },
    ...(showMilestone ? [{
      field: 'milestoneValue',
      headerName: 'Milestone',
      flex: 0.7,
      sortable: false,
      renderCell: (p) => p.value != null ? `Month ${p.value}` : '—',
    }] : []),
    {
      field: 'triggerDate',
      headerName: 'Trigger Date',
      flex: 0.9,
      renderCell: (p) => fmtDate(p.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      renderCell: (p) => {
        const { color, label } = statusChipProps(p.value);
        return (
          <Chip
            label={label}
            color={color}
            size="small"
            sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.7rem' }}
          />
        );
      },
    },
    {
      field: 'reviewedByName',
      headerName: 'Reviewed By',
      flex: 1,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title={p.value ?? ''}><span className="leave-ellipsis">{p.value || '—'}</span></Tooltip>
      ),
    },
    {
      field: 'reviewedAt',
      headerName: 'Reviewed At',
      flex: 0.9,
      renderCell: (p) => fmtDate(p.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1.2,
      sortable: false,
      renderCell: (p) => {
        if (p.row.reviewType === REVIEW_TYPE.QUARTERLY_PERFORMANCE_REVIEW) {
          if (p.row.status === REVIEW_STATUS.PENDING) {
            return (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => navigate(`/home/quarterly-review/${p.row.id}`, { state: { review: p.row } })}
                className="leave-action-btn"
                startIcon={<EditNoteRoundedIcon sx={{ fontSize: '15px !important' }} />}
                sx={{ minWidth: 88, textTransform: 'none' }}
              >
                Fill Form
              </Button>
            );
          }
          if (p.row.status === REVIEW_STATUS.EMPLOYEE_SUBMITTED) {
            return (
              <Button
                size="small"
                variant="outlined"
                color="info"
                onClick={() => navigate(`/home/quarterly-review/${p.row.id}`, { state: { review: p.row } })}
                className="leave-action-btn"
                startIcon={<EditNoteRoundedIcon sx={{ fontSize: '15px !important' }} />}
                sx={{ minWidth: 88, textTransform: 'none' }}
              >
                Edit Form
              </Button>
            );
          }
          if (p.row.status === REVIEW_STATUS.COMPLETED) {
            return (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => navigate(`/home/quarterly-review/${p.row.id}`, { state: { review: p.row } })}
                className="leave-action-btn"
                startIcon={<AssessmentRoundedIcon sx={{ fontSize: '15px !important' }} />}
                sx={{ minWidth: 88, textTransform: 'none' }}
              >
                View Form
              </Button>
            );
          }
        }
        return null;
      },
    },
  ], [showMilestone, navigate]);

  // ── Empty state ───────────────────────────────────────────────────────
  const renderEmptyState = () => (
    <div className="leave-empty-state">
      <div className="leave-empty-icon-wrap">
        <AssessmentRoundedIcon className="leave-empty-icon" />
      </div>
      <Typography variant="h6" className="leave-empty-title">No Reviews Found</Typography>
      <Typography className="leave-empty-subtitle">
        {status || debouncedSearch
          ? 'No reviews match your current filters.'
          : `No ${REVIEW_TYPE_LABELS[reviewType]} reviews found.`}
      </Typography>
    </div>
  );

  return (
    <div className="leave-page-container">
      <PageHeader
        title="Self Evaluation"
        right={
          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />

      {SnackbarComponent}

      <Paper elevation={0} className="leave-card">
        {/* ── Header strip ─────────────────────────────────────────── */}
        <div className="leave-card-header">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <MonitorHeartRoundedIcon sx={{ color: '#fff', fontSize: 20 }} />
            <Typography className="leave-card-header-title" variant="subtitle1">
              Self Evaluation
            </Typography>
          </Box>

          {/* Status filter + search */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPaginationModel((p) => ({ ...p, page: 0 })); }}
              size="small"
              displayEmpty
              renderValue={(val) => val === '' ? 'All Status' : REVIEW_STATUS_OPTIONS.find(o => o.value === val)?.label ?? val}
              sx={{ minWidth: 130, height: 34, bgcolor: '#fff', borderRadius: 1 }}
            >
              {REVIEW_STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
            <input
              type="text"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="leave-search-input"
            />
          </Box>
        </div>

        {/* ── Content ──────────────────────────────────────────────── */}
        {loading ? (
          <Box sx={{ p: 2 }}><TableSkeleton rows={5} columns={6} /></Box>
        ) : !loading && rowCount === 0 ? (
          renderEmptyState()
        ) : (
          <div className="leave-data-table">
            <div className="leave-data-grid">
              <DataGrid
                rows={records}
                columns={columns}
                getRowId={(row) => row.id}
                pagination
                paginationMode="server"
                sortingMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50]}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                disableRowSelectionOnClick
                sx={{
                  height: '100%',
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': { bgcolor: 'rgba(4,88,100,0.04)' },
                  '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                  '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(4,88,100,0.03)' },
                }}
              />
            </div>
          </div>
        )}
      </Paper>
    </div>
  );
};

export default SelfEvaluation;
